import { useState, useEffect } from 'react';
import PaymentsTab from './PaymentsTab';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import toast from 'react-hot-toast';

const getApiBaseUrl = () => {
  let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

interface Gym {
  id: string;
  name: string;
  displayName: string;
  email: string;
  address: string | null;
  tier: string;
  status: string;
  createdAt: string;
  owner: {
    id: string;
    name: string | null;
    displayName: string | null;
    email: string | null;
  };
  branches: Array<{
    id: string;
    name: string;
    displayName: string;
    city: string | null;
    status: string | null;
  }>;
  _count: {
    branches: number;
    memberships: number;
    userMappings: number;
    memberGymMappings: number;
  };
}

interface Followup {
  id: string;
  note: string;
  status: string;
  followupDate: string | null;
  createdAt: string;
  externalGymName?: string | null;
  externalGymContact?: string | null;
  externalPicName?: string | null;
  externalGymCity?: string | null;
  currentSoftware?: string | null;
  interestLevel?: string | null;
  gymSize?: string | null;
  subscription: {
    gym: {
      id: string;
      name: string;
      displayName: string;
      email: string;
      address: string | null;
    };
  } | null;
}

import SubscriptionPlansTab from './SubscriptionPlansTab';
import ManageInvoicesPage from './ManageInvoicesPage';
import ConfigurationTab from './ConfigurationTab';
import MessagingTab from './MessagingTab';
import ScheduledJobsTab from './ScheduledJobsTab';
import PricingShowcasePage from './PricingShowcasePage';
import PrivacyTab from './PrivacyTab';
import Pagination from '../../components/ui/Pagination';



export default function SuperAdminManagePage() {
  const { admin, token, signOut } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'gyms' | 'revenue' | 'stats' | 'notes' | 'plans' | 'invoices' | 'configuration' | 'messaging' | 'payments' | 'scheduledJobs' | 'prospects' | 'privacy'>('gyms');
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [loading, setLoading] = useState(true);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [gymPage, setGymPage] = useState(1);
  const [gymTotalPages, setGymTotalPages] = useState(1);
  const [gymTotalCount, setGymTotalCount] = useState(0);

  // Notes search debounced values (separate from input values)
  const [notesSearchName, setNotesSearchName] = useState('');
  const [notesSearchCity, setNotesSearchCity] = useState('');
  const [notesSearchNameInput, setNotesSearchNameInput] = useState('');
  const [notesSearchCityInput, setNotesSearchCityInput] = useState('');

  // Delete Gym Modal State
  const [deleteSummaryGym, setDeleteSummaryGym] = useState<any | null>(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isLoadingDeleteSummary, setIsLoadingDeleteSummary] = useState(false);
  const [isDeletingGym, setIsDeletingGym] = useState(false);

  // Add Note Modal State
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNoteData, setNewNoteData] = useState({
    externalGymName: '',
    externalGymContact: '',
    externalPicName: '',
    externalGymCity: '',
    currentSoftware: '',
    interestLevel: '',
    gymSize: '',
    note: '',
    status: 'lead',
    followupDate: ''
  });
  const [isCreatingNote, setIsCreatingNote] = useState(false);

  // Edit Note Modal State
  const [editingNote, setEditingNote] = useState<Followup | null>(null);
  const [editNoteData, setEditNoteData] = useState({
    externalGymName: '',
    externalGymContact: '',
    externalPicName: '',
    externalGymCity: '',
    currentSoftware: '',
    interestLevel: '',
    gymSize: '',
    note: '',
    status: 'lead',
    followupDate: '',
  });
  const [isSavingNote, setIsSavingNote] = useState(false);

  useEffect(() => {
    if (!admin || !token) {
      navigate('/saas/login');
      return;
    }
    fetchGyms();
    if (activeTab === 'notes') {
      fetchFollowups();
    }
  }, [admin, token, navigate, activeTab, statusFilter]);

  // Debounce gym list search
  useEffect(() => {
    if (admin && token) {
      const timeoutId = setTimeout(() => {
        setGymPage(1); // Reset to page 1 on search
        fetchGyms(1);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchName, searchCity]);

  // Debounce notes search — min 3 chars, clear if below threshold
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setNotesSearchName(notesSearchNameInput.length >= 3 ? notesSearchNameInput : '');
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [notesSearchNameInput]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setNotesSearchCity(notesSearchCityInput.length >= 3 ? notesSearchCityInput : '');
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [notesSearchCityInput]);

  // Fetch notes when search terms or status filter changes
  useEffect(() => {
    if (activeTab === 'notes' && admin && token) {
      fetchFollowups();
    }
  }, [notesSearchName, notesSearchCity]);

  const fetchGyms = async (page = 1) => {
    if (!token) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchName) params.append('name', searchName);
      if (searchCity) params.append('city', searchCity);
      params.append('page', page.toString());
      params.append('limit', '10');

      const response = await fetch(`${API_BASE_URL}/api/saas/gyms?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          signOut();
          navigate('/saas/login');
          return;
        }
        throw new Error('Failed to fetch gyms');
      }

      const data = await response.json();
      setGyms(data.data || []);
      if (data.pagination) {
        setGymPage(data.pagination.page);
        setGymTotalPages(data.pagination.totalPages);
        setGymTotalCount(data.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching gyms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGymClick = (gymId: string) => {
    navigate(`/saas/manage/gyms/${gymId}`);
  };

  const handleFollowupClick = (gymId: string) => {
    window.open(`/saas/manage/gyms/${gymId}`, '_blank');
  };

  const toggleGymStatus = async (gymId: string, currentStatus: string) => {
    if (!token) return;

    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const confirmMessage = `Are you sure you want to ${newStatus === 'inactive' ? 'disable' : 'enable'} access for this gym?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update gym status');

      toast.success(`Gym ${newStatus === 'inactive' ? 'disabled' : 'enabled'} successfully`);
      fetchGyms(); // Refresh list
    } catch (error) {
      console.error('Error toggling gym status:', error);
      toast.error('Failed to update gym status');
    }
  };

  const handleDeleteGymClick = async (e: React.MouseEvent, gymId: string) => {
    e.stopPropagation();
    if (!token) return;
    try {
      setIsLoadingDeleteSummary(true);
      setDeleteConfirmStep(false);
      setDeleteConfirmText('');
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/delete-summary`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch summary');
      const data = await response.json();
      setDeleteSummaryGym(data.data);
    } catch (error) {
      toast.error('Failed to load gym summary');
    } finally {
      setIsLoadingDeleteSummary(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!token || !deleteSummaryGym) return;
    try {
      setIsDeletingGym(true);
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${deleteSummaryGym.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete gym');
      toast.success(`"${deleteSummaryGym.displayName}" has been permanently deleted`);
      setDeleteSummaryGym(null);
      setDeleteConfirmStep(false);
      setDeleteConfirmText('');
      fetchGyms(gymPage);
    } catch (error) {
      toast.error('Failed to delete gym');
    } finally {
      setIsDeletingGym(false);
    }
  };

  const fetchFollowups = async () => {
    if (!token) return;

    try {
      setFollowupsLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (notesSearchName) params.append('gymName', notesSearchName);
      if (notesSearchCity) params.append('city', notesSearchCity);

      const response = await fetch(`${API_BASE_URL}/api/saas/followups?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          signOut();
          navigate('/saas/login');
          return;
        }
        throw new Error('Failed to fetch followups');
      }

      const data = await response.json();
      setFollowups(data.data || []);
    } catch (error) {
      console.error('Error fetching followups:', error);
      toast.error('Failed to fetch followups');
    } finally {
      setFollowupsLoading(false);
    }
  };

  const handleUpdateNote = async () => {
    if (!token || !editingNote) return;

    try {
      setIsSavingNote(true);
      const payload: any = {
        note: editNoteData.note,
        status: editNoteData.status,
        followupDate: editNoteData.followupDate || null,
      };
      // Only send external fields if this is an external note
      if (!editingNote.subscription) {
        payload.externalGymName = editNoteData.externalGymName;
        payload.externalGymContact = editNoteData.externalGymContact || null;
        payload.externalPicName = editNoteData.externalPicName || null;
        payload.externalGymCity = editNoteData.externalGymCity || null;
        payload.currentSoftware = editNoteData.currentSoftware || null;
        payload.interestLevel = editNoteData.interestLevel || null;
        payload.gymSize = editNoteData.gymSize || null;
      }

      const response = await fetch(`${API_BASE_URL}/api/saas/notes/${editingNote.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update note');
      }

      toast.success('Note updated successfully');
      setEditingNote(null);
      fetchFollowups();
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleCreateNote = async () => {
    if (!token) return;

    // Validate inputs
    if (!newNoteData.externalGymName || !newNoteData.note) {
      toast.error('Please fill in Gym Name and Note');
      return;
    }

    try {
      setIsCreatingNote(true);
      const response = await fetch(`${API_BASE_URL}/api/saas/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newNoteData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create note');
      }

      toast.success('Note created successfully');
      setShowAddNoteModal(false);
      setNewNoteData({
        externalGymName: '',
        externalGymContact: '',
        externalPicName: '',
        externalGymCity: '',
        currentSoftware: '',
        interestLevel: '',
        gymSize: '',
        note: '',
        status: 'lead',
        followupDate: ''
      });
      fetchFollowups();
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error('Failed to create note');
    } finally {
      setIsCreatingNote(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Super Admin</h1>
              <p className="text-sm text-gray-600">Manage gyms, revenue, and stats</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{admin?.email}</span>
              <button
                onClick={signOut}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('gyms')}
              className={`${activeTab === 'gyms'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Gyms
            </button>
            <button
              onClick={() => setActiveTab('revenue')}
              className={`${activeTab === 'revenue'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Revenue
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`${activeTab === 'stats'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Stats
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`${activeTab === 'notes'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('plans')}
              className={`${activeTab === 'plans'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Plans
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`${activeTab === 'invoices'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Invoices
            </button>
            <button
              onClick={() => setActiveTab('configuration')}
              className={`${activeTab === 'configuration'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('messaging')}
              className={`${activeTab === 'messaging'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Messaging
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`${activeTab === 'payments'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Payments
            </button>
            <button
              onClick={() => setActiveTab('scheduledJobs')}
              className={`${activeTab === 'scheduledJobs'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Scheduled Jobs
            </button>
            <button
              onClick={() => setActiveTab('prospects')}
              className={`${activeTab === 'prospects'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >Prospects</button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`${activeTab === 'privacy'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Privacy
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="py-6">
          {activeTab === 'gyms' && (
            <div>
              {/* Filters */}
              <div className="mb-6 flex gap-4">
                <div className="flex-1">
                  <label htmlFor="search-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Search by Name
                  </label>
                  <input
                    id="search-name"
                    type="text"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder="Gym name..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="search-city" className="block text-sm font-medium text-gray-700 mb-1">
                    Search by City
                  </label>
                  <input
                    id="search-city"
                    type="text"
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    placeholder="City..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Gyms List */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <p className="mt-2 text-gray-600">Loading gyms...</p>
                </div>
              ) : gyms.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No gyms found</p>
                </div>
              ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {gyms.map((gym) => (
                      <li key={gym.id}>
                        <div className="px-4 py-4 flex items-center justify-between sm:px-6 hover:bg-gray-50 transition-colors">
                          <div
                            className="flex-1 min-w-0 pr-4 cursor-pointer"
                            onClick={() => handleGymClick(gym.id)}
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-bold text-indigo-600 truncate">{gym.displayName}</p>
                              <div className="ml-2 flex-shrink-0 flex">
                                <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${gym.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                  {gym.status.charAt(0).toUpperCase() + gym.status.slice(1)}
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 sm:flex sm:justify-between">
                              <div className="sm:flex">
                                <p className="flex items-center text-xs text-gray-500">
                                  {gym.email}
                                </p>
                                <p className="mt-2 flex items-center text-xs text-gray-500 sm:mt-0 sm:ml-6">
                                  Owner: {gym.owner.displayName || gym.owner.email}
                                </p>
                                {gym.createdAt && (
                                  <p className="mt-2 flex items-center text-xs text-gray-400 sm:mt-0 sm:ml-6">
                                    Signup: {new Date(gym.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                  </p>
                                )}
                              </div>
                              <div className="mt-2 flex items-center text-xs text-gray-400 sm:mt-0">
                                <span className="mr-3">B: {gym._count.branches}</span>
                                <span className="mr-3">M: {gym._count.memberGymMappings}</span>
                                <span>T: {gym._count.userMappings}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGymStatus(gym.id, gym.status);
                              }}
                              className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${gym.status === 'active'
                                ? 'border-red-300 text-red-700 hover:bg-red-50'
                                : 'border-green-300 text-green-700 hover:bg-green-50'
                                }`}
                            >
                              {gym.status === 'active' ? 'Disable Access' : 'Enable Access'}
                            </button>
                            <button
                              onClick={(e) => handleDeleteGymClick(e, gym.id)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete Gym"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGymClick(gym.id);
                              }}
                              className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                              title="View Details"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {/* Pagination */}
                  <Pagination
                    page={gymPage}
                    totalPages={gymTotalPages}
                    totalCount={gymTotalCount}
                    pageSize={10}
                    onPageChange={fetchGyms}
                    accent="indigo"
                    className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'revenue' && (
            <div className="text-center py-12">
              <p className="text-gray-600">Revenue tab - Coming soon</p>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="text-center py-12">
              <p className="text-gray-600">Stats tab - Coming soon</p>
            </div>
          )}

          {activeTab === 'notes' && (
            <div>
              {/* Header row */}
              <div className="mb-4 flex flex-wrap gap-3 justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  Notes
                  {!followupsLoading && <span className="ml-2 text-base font-normal text-gray-500">({followups.length})</span>}
                </h2>
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  >
                    <option value="all">All Statuses</option>
                    <option value="lead">Lead</option>
                    <option value="pending">Pending</option>
                    <option value="addressed">Addressed</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button
                    onClick={() => setShowAddNoteModal(true)}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 font-medium whitespace-nowrap"
                  >
                    + Add Note
                  </button>
                </div>
              </div>

              {/* Search row */}
              <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <input
                    type="text"
                    value={notesSearchNameInput}
                    onChange={(e) => setNotesSearchNameInput(e.target.value)}
                    placeholder="Search by gym name (min 3 chars)..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {notesSearchNameInput.length > 0 && notesSearchNameInput.length < 3 && (
                    <span className="absolute right-3 top-2.5 text-xs text-amber-500">{3 - notesSearchNameInput.length} more</span>
                  )}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={notesSearchCityInput}
                    onChange={(e) => setNotesSearchCityInput(e.target.value)}
                    placeholder="Search by city (min 3 chars)..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  {notesSearchCityInput.length > 0 && notesSearchCityInput.length < 3 && (
                    <span className="absolute right-3 top-2.5 text-xs text-amber-500">{3 - notesSearchCityInput.length} more</span>
                  )}
                </div>
              </div>

              {/* Add Note Modal */}
              {showAddNoteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 overflow-y-auto z-50 flex items-start justify-center pt-10 px-4">
                  <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl p-6 mb-10">
                    <div className="flex justify-between items-center mb-5">
                      <h3 className="text-lg font-semibold text-gray-900">Add External Note</h3>
                      <button onClick={() => setShowAddNoteModal(false)} className="text-gray-400 hover:text-gray-600">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gym Name *</label>
                          <input
                            type="text"
                            value={newNoteData.externalGymName}
                            onChange={(e) => setNewNoteData({ ...newNoteData, externalGymName: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="e.g. Iron Fitness"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                          <input
                            type="text"
                            value={newNoteData.externalGymCity}
                            onChange={(e) => setNewNoteData({ ...newNoteData, externalGymCity: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="e.g. Chennai"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                          <input
                            type="text"
                            value={newNoteData.externalGymContact}
                            onChange={(e) => setNewNoteData({ ...newNoteData, externalGymContact: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Phone / Email"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">PIC Name</label>
                          <input
                            type="text"
                            value={newNoteData.externalPicName}
                            onChange={(e) => setNewNoteData({ ...newNoteData, externalPicName: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Person In Charge"
                          />
                        </div>
                      </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Interest Level</label>
                            <select
                              value={newNoteData.interestLevel}
                              onChange={(e) => setNewNoteData({ ...newNoteData, interestLevel: e.target.value })}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="">— Select —</option>
                              <option value="hot">🔥 Hot</option>
                              <option value="warm">☀️ Warm</option>
                              <option value="cold">❄️ Cold</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gym Size</label>
                            <select
                              value={newNoteData.gymSize}
                              onChange={(e) => setNewNoteData({ ...newNoteData, gymSize: e.target.value })}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="">— Select —</option>
                              <option value="small">Small</option>
                              <option value="medium">Medium</option>
                              <option value="large">Large</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Current Software</label>
                            <input
                              type="text"
                              value={newNoteData.currentSoftware}
                              onChange={(e) => setNewNoteData({ ...newNoteData, currentSoftware: e.target.value })}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="e.g. None, Excel, Zingfit"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Note *</label>
                          <textarea
                            value={newNoteData.note}
                            onChange={(e) => setNewNoteData({ ...newNoteData, note: e.target.value })}
                            rows={4}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Call details, requirements, pricing discussion..."
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                              value={newNoteData.status}
                              onChange={(e) => setNewNoteData({ ...newNoteData, status: e.target.value })}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="lead">Lead</option>
                              <option value="pending">Pending</option>
                              <option value="addressed">Addressed</option>
                              <option value="closed">Closed</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
                            <input
                              type="date"
                              value={newNoteData.followupDate}
                              onChange={(e) => setNewNoteData({ ...newNoteData, followupDate: e.target.value })}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          onClick={() => setShowAddNoteModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateNote}
                        disabled={isCreatingNote}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
                      >
                        {isCreatingNote ? 'Creating...' : 'Create Note'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Note Modal */}
              {editingNote && (
                <div className="fixed inset-0 bg-black bg-opacity-40 overflow-y-auto z-50 flex items-start justify-center pt-10 px-4">
                  <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl p-6 mb-10">
                    <div className="flex justify-between items-center mb-5">
                      <h3 className="text-lg font-semibold text-gray-900">Edit Note</h3>
                      <button onClick={() => setEditingNote(null)} className="text-gray-400 hover:text-gray-600">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-4">
                      {/* External-only fields */}
                      {!editingNote.subscription && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Gym Name *</label>
                              <input
                                type="text"
                                value={editNoteData.externalGymName}
                                onChange={(e) => setEditNoteData({ ...editNoteData, externalGymName: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                              <input
                                type="text"
                                value={editNoteData.externalGymCity}
                                onChange={(e) => setEditNoteData({ ...editNoteData, externalGymCity: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g. Chennai"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                              <input
                                type="text"
                                value={editNoteData.externalGymContact}
                                onChange={(e) => setEditNoteData({ ...editNoteData, externalGymContact: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">PIC Name</label>
                              <input
                                type="text"
                                value={editNoteData.externalPicName}
                                onChange={(e) => setEditNoteData({ ...editNoteData, externalPicName: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Interest Level</label>
                              <select
                                value={editNoteData.interestLevel}
                                onChange={(e) => setEditNoteData({ ...editNoteData, interestLevel: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                <option value="">— Select —</option>
                                <option value="hot">🔥 Hot</option>
                                <option value="warm">☀️ Warm</option>
                                <option value="cold">❄️ Cold</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Gym Size</label>
                              <select
                                value={editNoteData.gymSize}
                                onChange={(e) => setEditNoteData({ ...editNoteData, gymSize: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                <option value="">— Select —</option>
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Current Software</label>
                              <input
                                type="text"
                                value={editNoteData.currentSoftware}
                                onChange={(e) => setEditNoteData({ ...editNoteData, currentSoftware: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g. None, Excel, Zingfit"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Note *</label>
                        <textarea
                          value={editNoteData.note}
                          onChange={(e) => setEditNoteData({ ...editNoteData, note: e.target.value })}
                          rows={5}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select
                            value={editNoteData.status}
                            onChange={(e) => setEditNoteData({ ...editNoteData, status: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="lead">Lead</option>
                            <option value="pending">Pending</option>
                            <option value="addressed">Addressed</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
                          <input
                            type="date"
                            value={editNoteData.followupDate}
                            onChange={(e) => setEditNoteData({ ...editNoteData, followupDate: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setEditingNote(null)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateNote}
                        disabled={isSavingNote}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
                      >
                        {isSavingNote ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {followupsLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <p className="mt-2 text-gray-600">Loading notes...</p>
                </div>
              ) : followups.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {notesSearchName || notesSearchCity ? 'No notes match your search.' : 'No notes found.'}
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-52">Gym / Lead</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Follow-up</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {followups.map((followup) => (
                          <tr key={followup.id} className="hover:bg-gray-50 align-top">
                            <td className="px-4 py-3">
                              <div className="text-sm font-semibold text-gray-900 leading-tight">
                                {followup.subscription?.gym?.displayName || followup.externalGymName || 'Unknown Gym'}
                              </div>
                              {followup.externalGymCity && (
                                <div className="text-xs text-gray-500 mt-0.5">{followup.externalGymCity}</div>
                              )}
                              <div className="text-xs text-gray-400 mt-0.5">
                                {followup.subscription?.gym?.email || followup.externalGymContact || ''}
                              </div>
                              {followup.externalPicName && (
                                <div className="text-xs text-indigo-600 mt-0.5">PIC: {followup.externalPicName}</div>
                              )}
                              <div className="flex flex-wrap gap-1 mt-1">
                                {followup.interestLevel && (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    followup.interestLevel === 'hot' ? 'bg-red-100 text-red-700' :
                                    followup.interestLevel === 'warm' ? 'bg-orange-100 text-orange-700' :
                                    'bg-sky-100 text-sky-700'
                                  }`}>
                                    {followup.interestLevel === 'hot' ? '🔥' : followup.interestLevel === 'warm' ? '☀️' : '❄️'} {followup.interestLevel.charAt(0).toUpperCase() + followup.interestLevel.slice(1)}
                                  </span>
                                )}
                                {followup.gymSize && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                                    {followup.gymSize.charAt(0).toUpperCase() + followup.gymSize.slice(1)}
                                  </span>
                                )}
                                {followup.currentSoftware && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600" title="Current Software">
                                    {followup.currentSoftware}
                                  </span>
                                )}
                                {!followup.subscription && !followup.interestLevel && !followup.gymSize && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">External</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-800 whitespace-pre-wrap break-words max-w-xs line-clamp-3">{followup.note}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                followup.status === 'lead' ? 'bg-yellow-100 text-yellow-800' :
                                followup.status === 'addressed' ? 'bg-green-100 text-green-800' :
                                followup.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {followup.status.charAt(0).toUpperCase() + followup.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {followup.followupDate ? (
                                <div className="text-sm">
                                  <span className="text-gray-800">{new Date(followup.followupDate).toLocaleDateString()}</span>
                                  {new Date(followup.followupDate) < new Date() && (
                                    <span className="ml-1 text-xs text-red-600 font-medium">Overdue</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                              {new Date(followup.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditingNote(followup);
                                    setEditNoteData({
                                      externalGymName: followup.externalGymName || '',
                                      externalGymContact: followup.externalGymContact || '',
                                      externalPicName: followup.externalPicName || '',
                                      externalGymCity: followup.externalGymCity || '',
                                      currentSoftware: followup.currentSoftware || '',
                                      interestLevel: followup.interestLevel || '',
                                      gymSize: followup.gymSize || '',
                                      note: followup.note || '',
                                      status: followup.status || 'lead',
                                      followupDate: followup.followupDate ? new Date(followup.followupDate).toISOString().split('T')[0] : '',
                                    });
                                  }}
                                  className="text-indigo-600 hover:text-indigo-900 font-medium"
                                >
                                  Edit
                                </button>
                                {followup.subscription?.gym?.id && (
                                  <button
                                    onClick={() => handleFollowupClick(followup.subscription!.gym.id)}
                                    className="text-gray-500 hover:text-gray-800"
                                  >
                                    View →
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden space-y-3">
                    {followups.map((followup) => (
                      <div key={followup.id} className="bg-white rounded-lg shadow p-4">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {followup.subscription?.gym?.displayName || followup.externalGymName || 'Unknown Gym'}
                            </div>
                            {followup.externalGymCity && (
                              <div className="text-xs text-gray-500">{followup.externalGymCity}</div>
                            )}
                            {(followup.subscription?.gym?.email || followup.externalGymContact) && (
                              <div className="text-xs text-gray-400">{followup.subscription?.gym?.email || followup.externalGymContact}</div>
                            )}
                            <div className="flex flex-wrap gap-1 mt-1">
                              {followup.interestLevel && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  followup.interestLevel === 'hot' ? 'bg-red-100 text-red-700' :
                                  followup.interestLevel === 'warm' ? 'bg-orange-100 text-orange-700' :
                                  'bg-sky-100 text-sky-700'
                                }`}>
                                  {followup.interestLevel === 'hot' ? '🔥' : followup.interestLevel === 'warm' ? '☀️' : '❄️'} {followup.interestLevel.charAt(0).toUpperCase() + followup.interestLevel.slice(1)}
                                </span>
                              )}
                              {followup.gymSize && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                                  {followup.gymSize.charAt(0).toUpperCase() + followup.gymSize.slice(1)}
                                </span>
                              )}
                              {followup.currentSoftware && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                                  {followup.currentSoftware}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`flex-shrink-0 px-2 py-1 text-xs rounded-full font-medium ${
                            followup.status === 'lead' ? 'bg-yellow-100 text-yellow-800' :
                            followup.status === 'addressed' ? 'bg-green-100 text-green-800' :
                            followup.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {followup.status.charAt(0).toUpperCase() + followup.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-3 line-clamp-3">{followup.note}</p>
                        <div className="flex justify-between items-center text-xs text-gray-400">
                          <div>
                            {followup.followupDate ? (
                              <span className={new Date(followup.followupDate) < new Date() ? 'text-red-600 font-medium' : ''}>
                                Follow-up: {new Date(followup.followupDate).toLocaleDateString()}
                                {new Date(followup.followupDate) < new Date() && ' (Overdue)'}
                              </span>
                            ) : (
                              <span>No follow-up set</span>
                            )}
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                setEditingNote(followup);
                                setEditNoteData({
                                  externalGymName: followup.externalGymName || '',
                                  externalGymContact: followup.externalGymContact || '',
                                  externalPicName: followup.externalPicName || '',
                                  externalGymCity: followup.externalGymCity || '',
                                  currentSoftware: followup.currentSoftware || '',
                                  interestLevel: followup.interestLevel || '',
                                  gymSize: followup.gymSize || '',
                                  note: followup.note || '',
                                  status: followup.status || 'lead',
                                  followupDate: followup.followupDate ? new Date(followup.followupDate).toISOString().split('T')[0] : '',
                                });
                              }}
                              className="text-indigo-600 font-medium"
                            >
                              Edit
                            </button>
                            {followup.subscription?.gym?.id && (
                              <button
                                onClick={() => handleFollowupClick(followup.subscription!.gym.id)}
                                className="text-gray-500"
                              >
                                View Gym →
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'plans' && (
            <SubscriptionPlansTab token={token || ''} />
          )}

          {activeTab === 'invoices' && (
            <ManageInvoicesPage />
          )}

          {activeTab === 'configuration' && (
            <ConfigurationTab token={token || ''} />
          )}

          {activeTab === 'messaging' && (
            <MessagingTab token={token || ''} />
          )}

          {activeTab === 'payments' && (
            <PaymentsTab token={token || ''} />
          )}

          {activeTab === 'scheduledJobs' && (
            <ScheduledJobsTab token={token || ''} />
          )}

          {activeTab === 'prospects' && (
            <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-6">
              <PricingShowcasePage />
            </div>
          )}

          {activeTab === 'privacy' && (
            <PrivacyTab token={token || ''} />
          )}
        </div>
      </div>

      {/* Delete Gym Modal — rendered outside tab blocks so it works from any tab */}
      {(deleteSummaryGym || isLoadingDeleteSummary) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto z-50 flex items-start justify-center pt-10 px-4">
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl p-6 mb-10">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-semibold text-red-700">Delete Gym</h3>
              <button
                onClick={() => { setDeleteSummaryGym(null); setDeleteConfirmStep(false); setDeleteConfirmText(''); }}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >&times;</button>
            </div>

            {isLoadingDeleteSummary ? (
              <p className="text-center text-gray-500 py-8">Loading summary...</p>
            ) : deleteSummaryGym && !deleteConfirmStep ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  You are about to permanently delete <span className="font-semibold text-gray-900">{deleteSummaryGym.displayName}</span>. This action cannot be undone. The following data will be erased:
                </p>

                <div className="grid grid-cols-2 gap-2 mb-5">
                  {[
                    { label: 'Branches', value: deleteSummaryGym._count.branches },
                    { label: 'Members', value: deleteSummaryGym._count.memberGymMappings },
                    { label: 'Team Members', value: deleteSummaryGym._count.userMappings },
                    { label: 'Memberships', value: deleteSummaryGym._count.memberships },
                    { label: 'Purchases', value: deleteSummaryGym._count.gymPurchases },
                    { label: 'Plans', value: deleteSummaryGym._count.membershipPlans },
                    { label: 'Services', value: deleteSummaryGym._count.services },
                    { label: 'Classes', value: deleteSummaryGym._count.gymClasses },
                    { label: 'Class Bookings', value: deleteSummaryGym._count.classBookings },
                    { label: 'Attendance Logs', value: deleteSummaryGym._count.attendanceLogs },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-xs text-gray-500">{label}</span>
                      <span className="text-sm font-semibold text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>

                {deleteSummaryGym.branches.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Branches</p>
                    <ul className="space-y-1">
                      {deleteSummaryGym.branches.map((b: any) => (
                        <li key={b.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-1">
                          <span className="text-gray-800">{b.displayName}{b.city ? ` · ${b.city}` : ''}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{b.status || 'unknown'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  onClick={() => setDeleteConfirmStep(true)}
                  className="w-full py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Proceed to Delete
                </button>
              </>
            ) : deleteSummaryGym && deleteConfirmStep ? (
              <>
                <p className="text-sm text-gray-700 mb-4">
                  Type <span className="font-mono font-bold text-red-700">{deleteSummaryGym.displayName}</span> to confirm permanent deletion:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteSummaryGym.displayName}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-400"
                />
                <div className="flex space-x-3">
                  <button
                    onClick={() => setDeleteConfirmStep(false)}
                    className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleteConfirmText !== deleteSummaryGym.displayName || isDeletingGym}
                    className="flex-1 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isDeletingGym ? 'Deleting...' : 'Delete Permanently'}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}


